import axios from "axios";
import { TimeTrackerProvider } from "@/views";

const baseUrl = "/api/v0/connections";

export interface CreateConnectionModel {
    accountId: string;
    name: string;
    apiKey: string;
    provider: TimeTrackerProvider;
    providerUrl: string | null;
}

export interface ConnectionSyncedState {
    id: string;
    synced: boolean;
}

export interface SorexMappingData {
    id: string;
    name: string;
}

export interface SorexMappingClientData extends SorexMappingData {
    clockifyId: string;
    customerId: string | null;
    hasTimeEntries: boolean;
}

export interface SorexMappingEmployeeData extends SorexMappingData {
    clockifyId: string;
    invItemId: string | null;
    timerEmployeeId: string | null;
    employeeId: string | null;
    hasTimeEntries: boolean;
    excluded: boolean;
}

export interface SorexMappingModel {
    clients: SorexMappingClientData[];
    employees: SorexMappingEmployeeData[];
    customers: SorexMappingData[];
    invItems: SorexMappingData[];
    timerEmployees: SorexMappingData[];
}

export interface MappingUpdateModelItem {
    sourceId: string;
    targetId: string | null;
}

export interface ExclusionUpdateModelItem {
    sourceId: string;
    excluded: boolean;
}

export interface MappingUpdateModel {
    employeeToInvItem: MappingUpdateModelItem[];
    clientToCustomer: MappingUpdateModelItem[];
    employeeToEmployee: MappingUpdateModelItem[];
    employeeExclusions: ExclusionUpdateModelItem[];
}

export interface WorkspaceApiResult {
    name: string;
    id: string;
}

const connectionApi = {
    createConnection: async (model: CreateConnectionModel): Promise<string> => {
        const { data } = await axios.post<string>(baseUrl, model);
        return data;
    },

    deleteConnection: async (connectionId: string): Promise<void> => {
        await axios.delete(`${baseUrl}/${connectionId}`);
    },

    updateApiKey: async (connectionId: string, apiKey: string): Promise<void> => {
        await axios.patch(`${baseUrl}/${connectionId}/apikey`, { apiKey });
    },

    sync: async (connectionId: string): Promise<void> => {
        await axios.post(`${baseUrl}/${connectionId}/sync`);
    },

    syncedStatus: async (connectionIds: string[]): Promise<ConnectionSyncedState[]> => {
        const { data } = await axios.post<ConnectionSyncedState[]>(`${baseUrl}/synced`, connectionIds);
        return data;
    },

    getWorkspaces: async (connectionId: string): Promise<WorkspaceApiResult[]> => {
        const { data } = await axios.get<WorkspaceApiResult[]>(`${baseUrl}/${connectionId}/workspaces`);
        return data;
    },

    getMappingData: async (connectionId: string): Promise<SorexMappingModel> => {
        const { data } = await axios.get<SorexMappingModel>(`${baseUrl}/${connectionId}/map`);
        return data;
    },

    updateMappingData: async (connectionId: string, model: MappingUpdateModel): Promise<boolean> => {
        const { data } = await axios.patch<boolean>(`${baseUrl}/${connectionId}/map`, model);
        return data;
    },

    setEmployeeExcluded: async (connectionId: string, trackerId: string, excluded: boolean): Promise<void> => {
        await axios.patch(`${baseUrl}/${connectionId}/employees/${encodeURIComponent(trackerId)}`, { excluded });
    },
};

export type ConnectionApi = typeof connectionApi;

export default connectionApi;
